import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { brandColors } from '@config/brandTokens';
import {
  PUBLIC_SLUG_MIN,
  normalizePublicSlug,
  playerPublicPath,
  publicProfilePath,
  suggestPublicSlug,
  validatePublicSlug,
} from '@utils/publicProfileSlug';

type OwnerType = 'player' | 'academy';

interface PublicProfileUrlFieldProps {
  value: string;
  onChange: (slug: string) => void;
  exceptType?: OwnerType;
  exceptId?: string;
  suggestedFrom?: Array<string | null | undefined>;
  fallbackCode?: string | null;
  helperPrefix?: string;
}

export const PublicProfileUrlField: React.FC<PublicProfileUrlFieldProps> = ({
  value,
  onChange,
  exceptType,
  exceptId,
  suggestedFrom = [],
  fallbackCode,
  helperPrefix = 'encycgames.com/in/',
}) => {
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const slug = normalizePublicSlug(value);
  const suggestedKey = (suggestedFrom || []).filter(Boolean).join('|');
  const suggested = useMemo(() => suggestPublicSlug(...suggestedFrom), [suggestedKey]);
  const previewPath =
    exceptType === 'player'
      ? playerPublicPath(slug || value, fallbackCode)
      : publicProfilePath(slug || value, fallbackCode);

  useEffect(() => {
    if (!slug) {
      setAvailable(null);
      setMessage(null);
      return undefined;
    }
    const checked = validatePublicSlug(slug);
    if (!checked.ok) {
      setAvailable(false);
      setMessage(checked.message || 'Choose another name.');
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setChecking(true);
      try {
        const params = new URLSearchParams({ slug: checked.slug });
        if (exceptType) params.set('exceptType', exceptType);
        if (exceptId) params.set('exceptId', exceptId);
        const response = await fetch(`/api/public/profile-slug/available?${params}`);
        const data = await response.json();
        if (cancelled) return;
        setAvailable(!!data.available);
        setMessage(data.available ? 'This name is free. You can use it.' : 'Taken. Add letters or a number.');
      } catch {
        if (!cancelled) {
          setAvailable(null);
          setMessage('Could not check this name right now.');
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [slug, exceptType, exceptId]);

  return (
    <Box>
      <TextField
        fullWidth
        label="Profile name"
        value={value}
        onChange={(event) => onChange(normalizePublicSlug(event.target.value))}
        placeholder={suggested.length >= PUBLIC_SLUG_MIN ? suggested : 'firstname-lastname'}
        helperText={
          message
          || `At least ${PUBLIC_SLUG_MIN} letters or numbers. Hyphens are fine. If it is free, you can use it.`
        }
        error={available === false}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography variant="body2" sx={{ color: brandColors.slate, whiteSpace: 'nowrap' }}>
                {helperPrefix}
              </Typography>
            </InputAdornment>
          ),
          endAdornment: checking ? (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : undefined,
        }}
      />
      {previewPath ? (
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          href={previewPath}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mt: 0.75, textTransform: 'none' }}
        >
          View public page
        </Button>
      ) : null}
    </Box>
  );
};

export default PublicProfileUrlField;
